import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import AccentPicker from "@/components/AccentPicker";
import { Button } from "@/components/ui/button";
import { LogOut, Zap, Sun, Moon, Menu, X, MousePointer2 } from "lucide-react";
import { useAutoScrollPref } from "@/lib/globalAutoScroll";

const NAV = [
  { to: "/dashboard", label: "Home", tid: "nav-dashboard" },
  { to: "/dictation", label: "Dictation", tid: "nav-dictation" },
  { to: "/hindi-dictation", label: "आशु लिपिक", tid: "nav-hindi-dictation" },
  { to: "/legal", label: "Legal", tid: "nav-legal" },
  { to: "/pitman", label: "Pitman", tid: "nav-pitman" },
  { to: "/typing", label: "Typing", tid: "nav-typing" },
  { to: "/leaderboard", label: "Ranks", tid: "nav-leaderboard" },
  { to: "/badges", label: "Badges", tid: "nav-badges" },
  { to: "/mistakes", label: "Fix", tid: "nav-mistakes" },
  { to: "/race", label: "Race", tid: "nav-race" },
  { to: "/classes", label: "Classes", tid: "nav-classes" },
];

function NavLink({ to, label, tid, active, onClick }) {
  return (
    <Link
      to={to}
      data-testid={tid}
      onClick={onClick}
      className={`relative px-1.5 xl:px-2 2xl:px-3 py-2 text-[12px] xl:text-[13px] 2xl:text-[14.5px] uppercase tracking-[0.04em] 2xl:tracking-[0.09em] font-extrabold transition-all whitespace-nowrap rounded-md ${
        active
          ? "text-brand-gold"
          : "text-foreground/75 hover:text-foreground hover:bg-secondary/70"
      }`}
    >
      {label}
      {active && (
        <span className="absolute left-1/2 -translate-x-1/2 bottom-0 h-[3px] w-7 bg-brand-gold rounded-full" />
      )}
    </Link>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [autoScrollOn, setAutoScrollOn] = useAutoScrollPref();

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border/60">
      <div className="w-full px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        {/* Logo */}
        <Link to="/dashboard" className="flex items-center gap-2.5 shrink-0" data-testid="nav-logo">
          <div className="relative w-9 h-9 bg-brand-gold flex items-center justify-center rounded-lg animate-pulse-ring">
            <Zap className="w-4 h-4 text-background" strokeWidth={3} />
          </div>
          <div className="hidden 2xl:block leading-tight">
            <div className="font-display font-black text-base tracking-tight">StenoTypingBooster</div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-0 flex-1 justify-center">
          {NAV.map((n) => (
            <NavLink key={n.to} {...n} active={pathname.startsWith(n.to)} />
          ))}
          {user?.role === "admin" && (
            <NavLink
              to="/admin"
              label="Admin"
              tid="nav-admin"
              active={pathname.startsWith("/admin")}
            />
          )}
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-2 shrink-0">
          {/* Global auto-scroll master switch — controls source-follow, live
              highlighter caret scroll, and every future auto-scroll behaviour. */}
          <button
            onClick={() => setAutoScrollOn(!autoScrollOn)}
            aria-pressed={autoScrollOn}
            data-testid="nav-auto-scroll-btn"
            title={autoScrollOn ? "Auto-scroll is ON — click to disable everywhere" : "Auto-scroll is OFF — click to enable everywhere"}
            className={`h-9 px-2.5 border rounded-lg flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold transition-colors ${
              autoScrollOn
                ? "border-brand-emerald bg-brand-emerald/10 text-brand-emerald hover:bg-brand-emerald/20"
                : "border-border text-muted-foreground hover:bg-secondary"
            }`}
          >
            <MousePointer2 className="w-3.5 h-3.5" />
            <span className="hidden 2xl:inline">Auto-Scroll {autoScrollOn ? "ON" : "OFF"}</span>
          </button>

          {/* Theme toggle */}
          <button
            onClick={toggle}
            aria-label="Toggle theme"
            data-testid="theme-toggle"
            className="w-9 h-9 border border-border rounded-lg flex items-center justify-center text-foreground hover:bg-secondary transition-colors"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>

          {/* Accent color picker */}
          <AccentPicker />

          {/* User area */}
          <Link to="/profile" data-testid="nav-user-email" className="hidden 2xl:flex flex-col items-end text-right hover:text-brand-gold transition-colors">
            <span className="text-xs font-mono truncate max-w-[140px]">{user?.email}</span>
            <span className="text-[9px] uppercase tracking-widest text-brand-gold font-bold">{user?.role}</span>
          </Link>

          <Button
            onClick={doLogout}
            variant="outline"
            size="sm"
            data-testid="nav-logout-btn"
            className="hidden md:flex rounded-lg border-border hover:bg-brand-crimson/10 hover:text-brand-crimson hover:border-brand-crimson/40"
          >
            <LogOut className="w-4 h-4 2xl:mr-1" /> <span className="hidden 2xl:inline">Logout</span>
          </Button>

          {/* Mobile menu button */}
          <button
            className="lg:hidden w-9 h-9 border border-border rounded-lg flex items-center justify-center"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            data-testid="mobile-menu-btn"
          >
            {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Mobile menu drawer */}
      {open && (
        <div className="lg:hidden border-t border-border/60 bg-background/95 backdrop-blur-xl">
          <nav className="max-w-7xl mx-auto px-4 py-4 grid grid-cols-2 gap-1">
            {NAV.map((n) => (
              <NavLink key={n.to} {...n} active={pathname.startsWith(n.to)} onClick={() => setOpen(false)} />
            ))}
            {user?.role === "admin" && (
              <NavLink to="/admin" label="Admin" tid="nav-admin" active={pathname.startsWith("/admin")} onClick={() => setOpen(false)} />
            )}
            <button onClick={doLogout} className="col-span-2 mt-2 h-10 border border-border rounded-lg text-xs uppercase tracking-widest font-bold text-brand-crimson">
              <LogOut className="w-4 h-4 inline mr-2" /> Logout
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
